import React from "react";
import { Box, Typography, Button, Grid, Paper } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import WorkIcon from "@mui/icons-material/Work";
import PeopleIcon from "@mui/icons-material/People";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SecurityIcon from "@mui/icons-material/Security";

const Home = () => {
  const navigate = useNavigate();

  const handleButtonClick = () => {
    const userToken = sessionStorage.getItem("token");
    const isAuthenticated = userToken && userToken.token;
    if (isAuthenticated) {
      navigate("/jobs");
    } else {
      navigate("/auth/signup");
    }
  };

  const features = [
    {
      icon: <WorkIcon sx={{ fontSize: 40, color: "#00786F" }} />,
      title: "Browse Jobs",
      description:
        "Access thousands of job listings from top companies across various industries.",
    },
    {
      icon: <PeopleIcon sx={{ fontSize: 40, color: "#00786F" }} />,
      title: "Connect with Employers",
      description:
        "Build your professional network and connect with hiring managers directly.",
    },
    {
      icon: <TrendingUpIcon sx={{ fontSize: 40, color: "#00786F" }} />,
      title: "Career Growth",
      description:
        "Find opportunities that match your skills and accelerate your career journey.",
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 40, color: "#00786F" }} />,
      title: "Secure Platform",
      description:
        "Your data is properly protected with industry-standard security measures.",
    },
  ];

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        backgroundColor: "#f5f7fa",
        margin: 0,
        padding: 0,
      }}
    >
      {/* Hero Section */}
      <Box
        sx={{
          background:
            "linear-gradient(135deg, #003d38 0%, #00786F 50%, #00a896 100%)",
          minHeight: { xs: "80vh", md: "90vh" },
          display: "flex",
          alignItems: "center",
          position: "relative",
          overflow: "hidden",
          width: "100%",
        }}
      >
        {/* Decorative circles */}
        <Box
          sx={{
            position: "absolute",
            top: -100,
            right: -100,
            width: 300,
            height: 300,
            borderRadius: "50%",
            backgroundColor: "rgba(255,215,0,0.05)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: -50,
            left: -50,
            width: 200,
            height: 200,
            borderRadius: "50%",
            backgroundColor: "rgba(255,215,0,0.03)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 500,
            height: 500,
            borderRadius: "50%",
            backgroundColor: "rgba(255,215,0,0.02)",
          }}
        />

        {/* Mobile-only: image behind text */}
        <Box
          sx={{
            display: { xs: "block", md: "none" },
            position: "absolute",
            top: 0,
            right: 0,
            width: "100%",
            height: "100%",
            zIndex: 0,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              width: "100%",
              height: "100%",
              // Dark overlay so text stays readable over the image.
              background:
                "linear-gradient(to right, rgba(0,61,56,0.92) 30%, rgba(0,61,56,0.55) 100%)",
              zIndex: 1,
            }}
          />
          <Box
            component="img"
            src="https://i.ibb.co/mh7GRRf/Man-Searching-for-a-Job-removebg.png"
            alt=""
            aria-hidden="true"
            sx={{
              position: "absolute",
              right: -20,
              bottom: 0,
              height: "90%",
              width: "auto",
              objectFit: "contain",
              opacity: 0.6,
            }}
          />
        </Box>

        <Box
          sx={{
            width: "100%",
            px: { xs: 3, md: 4, lg: 6 },
            position: "relative",
            zIndex: 1,
          }}
        >
          <Grid container spacing={4} alignItems="center">
            {/* Left: text */}
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <Box sx={{ color: "white" }}>
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: "2rem", md: "3.5rem" },
                      lineHeight: 1.2,
                      mb: 2,
                      mt: 1,
                    }}
                  >
                    Looking for a new job?{" "}
                    <Box component="span" sx={{ color: "#FFD700" }}>
                      We've got you covered.
                    </Box>
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: "1.1rem",
                      opacity: 0.9,
                      mb: 4,
                      maxWidth: "500px",
                      lineHeight: 1.8,
                    }}
                  >
                    Create a free profile by signing up to view open positions
                    and start browsing jobs today. Your next career opportunity
                    is just a click away.
                  </Typography>

                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleButtonClick}
                    sx={{
                      backgroundColor: "#FFD700",
                      color: "#00786F",
                      fontWeight: 700,
                      fontSize: "1.1rem",
                      px: 4,
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: "none",
                      "&:hover": { transform: "scale(1.05)" },
                      transition:
                        "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                    }}
                  >
                    Start Searching for Jobs
                  </Button>

                  <Box sx={{ display: "flex", gap: 3, mt: 4 }}>
                    {[
                      { value: "10k+", label: "Job Listings" },
                      { value: "500+", label: "Companies" },
                      { value: "98%", label: "Satisfaction Rate" },
                    ].map((stat) => (
                      <Box key={stat.label}>
                        <Typography
                          variant="h4"
                          sx={{ fontWeight: 700, color: "#FFF" }}
                        >
                          {stat.value}
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.7 }}>
                          {stat.label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </motion.div>
            </Grid>

            {/* Right: image — desktop only */}
            <Grid
              item
              xs={12}
              md={6}
              sx={{ display: { xs: "none", md: "block" } }}
            >
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    position: "relative",
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      width: "80%",
                      height: "80%",
                      background:
                        "radial-gradient(circle, rgba(255,215,0,0.1) 0%, transparent 70%)",
                      borderRadius: "50%",
                    }}
                  />
                  <img
                    src="https://i.ibb.co/mh7GRRf/Man-Searching-for-a-Job-removebg.png"
                    alt="A professional man searching for a job, representing career opportunities"
                    style={{
                      maxWidth: "100%",
                      height: "auto",
                      position: "relative",
                      zIndex: 1,
                      filter: "drop-shadow(0 8px 32px rgba(0,0,0,0.2))",
                    }}
                  />
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Features Section */}
      <Box sx={{ width: "100%", backgroundColor: "#f5f7fa" }}>
        <Box
          sx={{
            width: "100%",
            px: { xs: 3, md: 4, lg: 6 },
            py: { xs: 8, md: 12 },
          }}
        >
          <Box sx={{ textAlign: "center", mb: 8 }}>
            <Typography
              variant="overline"
              sx={{
                color: "#00786F",
                fontWeight: 600,
                letterSpacing: "2px",
                textTransform: "uppercase",
              }}
            >
              Why Choose Us
            </Typography>
            <Typography
              variant="h3"
              sx={{ fontWeight: 700, color: "#003d38", mb: 2 }}
            >
              Everything you need to find your dream job
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "#666", maxWidth: "600px", mx: "auto" }}
            >
              Le-Travaille connects job seekers with top employers, making the
              job search process simple and effective.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      textAlign: "center",
                      borderRadius: 3,
                      height: "100%",
                      backgroundColor: "#FFF",
                      transition:
                        "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        width: 64,
                        height: 64,
                        borderRadius: "50%",
                        backgroundColor: "rgba(0,120,111,0.08)",
                        margin: "0 auto",
                        mb: 2,
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, color: "#003d38", mb: 1 }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </Paper>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #003d38 0%, #00786F 100%)",
          width: "100%",
        }}
      >
        <Box
          sx={{
            width: "100%",
            px: { xs: 3, md: 4, lg: 6 },
            py: { xs: 8, md: 10 },
          }}
        >
          <Box sx={{ textAlign: "center", color: "white" }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
              Ready to start your job search?
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9, mb: 4 }}>
              Join thousands of job seekers who found their dream jobs through
              Le-Travaille.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={handleButtonClick}
              sx={{
                backgroundColor: "#FFD700",
                color: "#003d38",
                fontWeight: 700,
                fontSize: "1.1rem",
                px: 4,
                py: 1.5,
                borderRadius: 2,
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#ffc107",
                  transform: "scale(1.05)",
                  boxShadow: "0 4px 20px rgba(255,215,0,0.3)",
                },
                transition:
                  "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
              }}
            >
              Get Started Now
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Home;
